/**
 * Métricas (seção 16).
 *
 * A tela mostra a nota de cada número junto do número, e não escondida atrás de
 * um ícone: "associado" e "gerado" são coisas diferentes, e quem lê o painel
 * precisa ver a diferença sem procurar.
 */
import { useState } from 'react';
import { useResource } from '../lib/useResource.js';
import { formatBRL, todayLocal } from '../lib/format.js';
import { Alert, Card, Empty, ErrorBox, PageHeader } from '../components/ui.js';
import { useSession } from '../lib/session.js';

interface Metrica {
  chave: string;
  rotulo: string;
  valor: number;
  formato: 'numero' | 'dinheiro';
  nota: string;
}
interface Relatorio {
  de: string;
  ate: string;
  janelaDeAtribuicaoDias: number;
  grupos: { titulo: string; metricas: Metrica[] }[];
  etapasEnviadas: { etapa: number; enviadas: number; aceitas: number }[];
  aviso: string;
}

/** Primeiro dia do mês corrente, no formato aceito pelo <input type="date">. */
function inicioDoMes(): string {
  return `${todayLocal().slice(0, 7)}-01`;
}

export function Metricas() {
  const { can } = useSession();
  const [de, setDe] = useState(inicioDoMes);
  const [ate, setAte] = useState(todayLocal);

  const periodoValido = de <= ate;
  const { data, error, loading } = useResource<Relatorio>(
    can('reports.read') && periodoValido
      ? `/api/metrics?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`
      : null,
    [de, ate],
  );

  if (!can('reports.read')) {
    return (
      <>
        <PageHeader title="Métricas" />
        <Card>
          <Empty title="Você não tem permissão para ver métricas." />
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Métricas"
        description="Números do período escolhido. Cada indicador traz a explicação do que ele conta."
      />

      <div className="toolbar">
        <label className="periodo">
          <span>De</span>
          <input type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} />
        </label>
        <label className="periodo">
          <span>Até</span>
          <input type="date" value={ate} min={de} onChange={(e) => setAte(e.target.value)} />
        </label>
        <button type="button" onClick={() => { setDe(inicioDoMes()); setAte(todayLocal()); }}>
          Mês corrente
        </button>
        {loading ? <span className="muted small">Carregando…</span> : null}
      </div>

      {!periodoValido ? (
        <Alert tone="warning" title="Período inválido">
          A data inicial precisa ser anterior ou igual à final.
        </Alert>
      ) : null}

      <ErrorBox error={error} />

      {data ? (
        <>
          <Alert tone="info" title="Como ler estes números">
            {data.aviso}
          </Alert>

          {data.grupos.map((grupo) => (
            <Card key={grupo.titulo} title={grupo.titulo}>
              <div className="grid cols-3">
                {grupo.metricas.map((m) => (
                  <div className="indicator" key={m.chave}>
                    <div className="value">
                      {m.formato === 'dinheiro' ? formatBRL(m.valor) : m.valor}
                    </div>
                    <div className="label">{m.rotulo}</div>
                    <div className="note">{m.nota}</div>
                  </div>
                ))}
              </div>
            </Card>
          ))}

          <Card
            title="Envios por etapa da sequência"
            description="Quantas mensagens saíram em cada etapa e quantas viraram aceite no período."
          >
            {data.etapasEnviadas.length === 0 ? (
              <Empty title="Nenhum lembrete enviado neste período." />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Etapa</th>
                      <th className="num">Enviadas</th>
                      <th className="num">Aceitas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.etapasEnviadas.map((e) => (
                      <tr key={e.etapa}>
                        <td>Etapa {e.etapa}</td>
                        <td className="num">{e.enviadas}</td>
                        <td className="num">{e.aceitas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="hint">
              Aceite é a resposta do cliente que confirma interesse — não é prova de que o serviço
              aconteceu. O serviço só entra nas contagens quando alguém registra o serviço aqui.
            </p>
          </Card>
        </>
      ) : null}
    </>
  );
}
